import {
  type EditAreaAdminState,
  editAreaAdminStateSchema,
  type SaveEditAreaInput,
} from '@repo/api-contracts';
import type { StoreApi } from 'zustand';
import {
  assembleEditAreaInput,
  type EditAreaDraftState,
  unsupportedSaveMessage,
} from '@/entities/edit-area';
import type { ProductSnapshot } from '@/entities/product';
import { describeEditAreaIssue } from './describe-edit-area-issue';

interface SessionActionPorts {
  readonly cancelQuery: () => Promise<void>;
  readonly setQuery: (state: EditAreaAdminState) => void;
  readonly fetchSnapshot: () => Promise<ProductSnapshot>;
  readonly save: (input: SaveEditAreaInput) => Promise<EditAreaAdminState>;
  readonly undo: (input: { productId: string }) => Promise<EditAreaAdminState>;
  readonly redo: (input: { productId: string }) => Promise<EditAreaAdminState>;
  readonly afterSave: () => void;
  readonly isActive: () => boolean;
}

/** 명시적인 저장·커서 이동만 편집 스냅샷을 교체한다. */
export const runEditAreaSessionAction = async (
  store: StoreApi<EditAreaDraftState>,
  ports: SessionActionPorts,
  action: 'save' | 'undo' | 'redo',
): Promise<boolean> => {
  const initial = store.getState();
  if (initial.locked || !ports.isActive()) return false;
  const unsupported = unsupportedSaveMessage(initial.unsupportedReasons);
  if (action === 'save' && unsupported !== null) throw new Error(unsupported);
  const { generation } = initial;
  const productId = initial.draft.productId;
  const current = (): boolean => ports.isActive() && store.getState().generation === generation;
  store.getState().setLocked(true);
  try {
    await ports.cancelQuery();
    if (!current()) return false;
    let result: EditAreaAdminState;
    if (action === 'save') {
      const snapshot = await ports.fetchSnapshot();
      if (!current()) return false;
      store.getState().reconcile(snapshot.colorOptionIds);
      // 그 사이 상품 실측이 바뀌었으면 조립이 불일치로 막고, 화면은 교체된 실측으로 갱신 안내를 띄운다.
      store.getState().setMeasurement(snapshot.measurement);
      const input = assembleEditAreaInput(store.getState());
      if (!input.success) {
        // 저장 전 확인 목록과 같은 문구 — zod 기본 영어 문구는 그 위치의 한국어 안내로 바꾸고 같은 안내는 한 번만 보인다.
        const messages = input.issues.map(
          (issue) => describeEditAreaIssue(issue, store.getState()).message,
        );
        throw new Error([...new Set(messages)].join(' · '));
      }
      result = await ports.save(input.data);
    } else {
      result = await ports[action]({ productId });
    }
    if (!current()) return false;
    const parsed = editAreaAdminStateSchema.parse(result);
    if (parsed.viewing.productId !== productId) throw new Error('다른 상품의 편집영역 응답입니다.');
    await ports.cancelQuery();
    if (!current()) return false;
    store.getState().applyServerState(parsed);
    ports.setQuery(parsed);
    if (action === 'save') ports.afterSave();
    return true;
  } finally {
    if (current()) store.getState().setLocked(false);
  }
};
